'use client';

import { motion } from 'framer-motion';
import { useId } from 'react';
import { CTA } from '@/components/CTA/CTA';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';

const fadeInUp = {
	initial: { opacity: 0, y: 20 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.5 },
};

const staggerContainer = {
	animate: {
		transition: {
			staggerChildren: 0.1,
		},
	},
};

interface HeadingCTAProps {
	heading: string;
	sub_heading?: string;
	content?: string;
	CTAs?: components['schemas']['SharedButtonComponent'][];
	backgroundColor?: string;
	mainCTA?: components['schemas']['SharedButtonComponent'];
	isFirst?: boolean;
}

export const HeadingCTA = ({
	heading,
	sub_heading,
	content,
	CTAs = [],
	backgroundColor,
	mainCTA,
	isFirst = false,
}: HeadingCTAProps) => {
	const SectionHeading = getSectionMotionHeading(isFirst);
	// Process CTAs to ensure it's always an array
	const buttons = Array.isArray(CTAs) ? CTAs : CTAs ? [CTAs] : [];
	const headingId = useId();

	return (
		<section
			role="region"
			aria-labelledby={heading ? headingId : undefined}
			className={cn(
				'heading-cta-section container relative mx-auto overflow-hidden py-8 text-center sm:py-8',
				backgroundColor,
			)}>
			<noscript>
				<style>{`
					.heading-cta-section h2[style],
					.heading-cta-section h3[style],
					.heading-cta-section p[style],
					.heading-cta-section div[style*='opacity'],
					.heading-cta-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<motion.div
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, margin: '-100px' }}
				variants={staggerContainer}>
				{heading && (
					<SectionHeading
						id={headingId}
						variants={fadeInUp}
						className={cn(
							'mb-4 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl',
							backgroundColor &&
								backgroundColor === 'bg-[#4338CA]' &&
								'text-white',
						)}>
						{heading}
					</SectionHeading>
				)}
				{sub_heading && (
					<motion.p
						variants={fadeInUp}
						className={cn(
							'mx-auto mb-8 w-full text-lg md:text-xl',
							backgroundColor === 'bg-[#4338CA]'
								? 'text-white'
								: 'text-gray-600',
						)}>
						{sub_heading}
					</motion.p>
				)}
				{content && (
					<motion.div
						variants={fadeInUp}
						className={cn(
							'prose mx-auto mb-8 max-w-3xl',
							backgroundColor === 'bg-[#4338CA]'
								? 'prose-invert'
								: 'prose-gray',
						)}
						dangerouslySetInnerHTML={{ __html: content }}
					/>
				)}
				<motion.div
					variants={staggerContainer}
					className="flex flex-col items-center justify-center gap-8">
					{buttons.length > 0 && (
						<motion.div
							variants={fadeInUp}
							className="flex flex-col items-center justify-center gap-4 sm:flex-row">
							<CTA CTAs={buttons} />
						</motion.div>
					)}
					{mainCTA && (
						<motion.div
							variants={fadeInUp}
							className="flex items-center justify-center">
							<CTA CTAs={[mainCTA]} />
						</motion.div>
					)}
				</motion.div>
			</motion.div>
		</section>
	);
};
